import { EntityType } from '@crew/enums/domain'
import { RelatedSuggestion } from '@crew/models/domain'
import { useTranslation } from '@crew/modules/i18n'
import { useSubscribeTaskRelatedInfo } from '@crew/providers/websocket'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewAiAssistantAvatar } from 'components/elements/crewAiAssistantAvatar/crewAiAssistantAvatar'
import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { Modal } from 'components/layouts/modal/modal'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'
import { FC, memo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useTaskDetailRelatedInfoListContext } from '../../taskDetailRelatedInfoList'
import { SuggestionTable } from '../taskDetailRelatedInfoListPanel/components/suggestionTable/suggestionTable'
import { useTaskDetailRelatedInfoListToolbar } from '../taskDetailRelatedInfoListToolbar/useTaskDetailRelatedInfoListToolbar'

export const TaskDetailRelatedInfoSuggestionDialog: FC = memo(() => {
  const { t } = useTranslation()
  const { taskId } = useParams()
  const [showApiErrors] = useShowApiErrors()
  const toast = useToast()

  if (!taskId) {
    throw new Error('taskId is undefined')
  }

  const {
    displayState,
    setDisplayState,
    setRelatedSuggestions,
    setSelectedSuggestions,
    relatedSuggestions,
    selectedSuggestions,
  } = useTaskDetailRelatedInfoListContext()

  const { insertEntityReferences, isLoadingInsertEntityReferencesMutation } =
    useTaskDetailRelatedInfoListToolbar()

  const showSearching = displayState === 'searching'
  const showSuggestionTable = displayState === 'suggestion'
  const showEmptySuggestion =
    showSuggestionTable && relatedSuggestions.length === 0
  const isOpenDialog = displayState !== 'relatedInfo'

  // ダイアログを閉じるときに関連提案状態を初期化する
  const handleCloseDialog = useCallback(() => {
    setSelectedSuggestions([])
    setRelatedSuggestions([])
    setDisplayState('relatedInfo')
  }, [setDisplayState, setRelatedSuggestions, setSelectedSuggestions])

  // 関連情報提案を登録する
  const handleAddRelatedInfoButtonClick = useCallback(async () => {
    try {
      await insertEntityReferences(selectedSuggestions)
      toast.success(t('message.entityReference.entityReferenceCreated'))
      handleCloseDialog()
    } catch (err) {
      showApiErrors(err)
    }
  }, [
    handleCloseDialog,
    insertEntityReferences,
    selectedSuggestions,
    showApiErrors,
    t,
    toast,
  ])

  // タスク関連情報提案作成完了したときのコールバック関数
  const handleTaskRelatedSuggestionCreated = useCallback(
    (relatedSuggestion: RelatedSuggestion) => {
      if (displayState === 'relatedInfo') {
        return
      }

      // 受信したsuggestionが現在表示しているタスクのものか確認
      if (
        relatedSuggestion.sourceEntityType !== EntityType.Task ||
        relatedSuggestion.sourceEntityRecordId !== taskId
      ) {
        return
      }

      const relatedItems = relatedSuggestion.relatedItems ?? []

      // 提案された関連先が空の場合はダイアログ上で「見つかりませんでした」を表示する
      if (relatedItems.length === 0) {
        setRelatedSuggestions([])
        setSelectedSuggestions([])
        setDisplayState('suggestion')
        return
      }

      const suggestionItems = relatedItems.map(
        ({ entityType, entityRecordId, title, updatedBy, updatedAt }) => ({
          id: `${entityType}|${entityRecordId}`,
          entityType,
          entityRecordId,
          title,
          updatedBy,
          updatedAt,
        })
      )
      setRelatedSuggestions(suggestionItems)
      setDisplayState('suggestion')
    },
    [
      displayState,
      setDisplayState,
      setRelatedSuggestions,
      setSelectedSuggestions,
      taskId,
    ]
  )

  // タスク関連情報提案作成失敗したときのコールバック関数
  const handleTaskRelatedSuggestionFailed = useCallback(
    (relatedSuggestion: RelatedSuggestion) => {
      if (displayState === 'relatedInfo') {
        return
      }

      // 提案失敗通知が現在表示しているタスクのものか確認
      if (
        relatedSuggestion.sourceEntityType !== EntityType.Task ||
        relatedSuggestion.sourceEntityRecordId !== taskId
      ) {
        return
      }

      setRelatedSuggestions([])
      setSelectedSuggestions([])
      setDisplayState('relatedInfo')
      toast.error(t('message.entityReference.relatedSuggestionCreateFailed'))
    },
    [
      displayState,
      setDisplayState,
      setRelatedSuggestions,
      setSelectedSuggestions,
      t,
      taskId,
      toast,
    ]
  )

  // タスクの関連情報の提案結果をsubscribeする
  useSubscribeTaskRelatedInfo(
    handleTaskRelatedSuggestionCreated,
    handleTaskRelatedSuggestionFailed
  )

  return (
    <Modal
      id="task-detail-related-info-suggestion-dialog"
      title={t('label.addRelatedInfoDialogTitle')}
      isOpen={isOpenDialog}
      onClose={handleCloseDialog}
      closeDisabled={showSearching}
    >
      <div
        className={
          showEmptySuggestion
            ? 'flex flex-col gap-y-3'
            : 'flex flex-col gap-y-3 h-full min-h-0 max-h-[640px]'
        }
      >
        {showSearching && (
          <div className="flex flex-1 flex-row items-center justify-center gap-3">
            <CrewAiAssistantAvatar size={CrewAvatarSize.base} />
            {t('label.aiIsSearchingRelatedInfo')}
          </div>
        )}

        {showSuggestionTable && (
          <>
            {relatedSuggestions.length > 0 ? (
              <div className="min-h-0 overflow-y-auto">
                <SuggestionTable />
              </div>
            ) : (
              <div className="py-6 text-center">
                {t('message.entityReference.relatedSuggestionNotFound')}
              </div>
            )}
            <div className="ml-auto flex flex-row items-center gap-x-2.5">
              <CrewButton
                id="task-detail-related-info-list-dialog-add-related-info-button"
                type="primary"
                text={t('action.addRelatedInformation')}
                onClick={handleAddRelatedInfoButtonClick}
                disabled={
                  selectedSuggestions.length === 0 ||
                  isLoadingInsertEntityReferencesMutation
                }
              />
              <CrewButton
                id="task-detail-related-info-list-dialog-cancel-adding-related-info-button"
                type="normal"
                stylingMode="outlined"
                text={t('action.cancel')}
                onClick={handleCloseDialog}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  )
})
